import React, { useEffect, useRef, useState } from 'react';

const SelectField = ({
    id,
    label,
    name,
    value,
    onChange,
    options,
    placeholder,
    labelClass,
    hint,
    isDynamic = false,
    multiple = false,
    required = false,
    disabled = false,
}) => {

    // console.log("hint", hint);
    const [selectedValues, setSelectedValues] = useState(
        isDynamic && multiple ? [[]] : (multiple ? [] : value)
    );
    const [showHint, setShowHint] = useState(false);
    const hintRef = useRef(null);
    const labelClasses = `${labelClass || ''} mb-0`;

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (hintRef.current && !hintRef.current.contains(e.target)) {
                setShowHint(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleChange = (e) => {
        const { value: selectedValue } = e.target;

        if (isDynamic && multiple) {
            const updatedValues = [...selectedValues];
            if (selectedValue) {
                updatedValues[updatedValues.length - 1] = selectedValue;
            } else {
                updatedValues.pop();
            }
            setSelectedValues(updatedValues);
            onChange(id, updatedValues);
        } else if (multiple) {
            const updatedValues = [...selectedValues];
            if (updatedValues.includes(selectedValue)) {
                updatedValues.splice(updatedValues.indexOf(selectedValue), 1);
            } else {
                updatedValues.push(selectedValue);
            }
            setSelectedValues(updatedValues);
            onChange(id, updatedValues);
        } else {
            setSelectedValues(selectedValue);
            onChange(id, selectedValue);
        }
    };

    const handleAddField = () => {
        const updated = [...selectedValues, []];
        setSelectedValues(updated);
        onChange(id, updated);
    };

    const handleRemoveField = (index) => {
        const updated = selectedValues.filter((_, i) => i !== index);
        setSelectedValues(updated);
        onChange(id, updated);
    };

    return (
        <div>
            {label && (
                <div className="d-inline-flex align-items-center gap-2 mb-1 position-relative flex-nowrap">
                    <label htmlFor={id} className={labelClasses}>
                        {label}
                        {required && <span className="text-danger ms-1">*</span>}
                    </label>

                    {hint && (
                        <div className="position-relative" ref={hintRef}>
                            <span
                                role="button"
                                tabIndex={0}
                                className="text-muted"
                                onClick={() => setShowHint(!showHint)}
                                onKeyDown={(e) => e.key === 'Enter' && setShowHint(!showHint)}
                                style={{ cursor: 'pointer' }}
                                aria-label="Show hint"
                            >
                                ℹ️
                            </span>
                            {showHint && (
                                <div
                                    className="position-absolute bg-white border shadow rounded p-2"
                                    style={{
                                        top: '120%',
                                        left: 0,
                                        width: '260px',
                                        zIndex: 3000,
                                    }}
                                >
                                    <div className="small text-muted">{hint}</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {isDynamic && multiple ? (
                <div>
                    {selectedValues.map((group, index) => (
                        <div key={index} className="mb-2">
                            <select
                                id={`${id}-${index}`}
                                name={name}
                                value={group || ''}
                                disabled={disabled}
                                onChange={handleChange}
                                required={required}
                                className="form-select"
                            >
                                <option value="" disabled>
                                    {placeholder || `Select ${label || name}`}
                                </option>
                                {options.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>

                            <button
                                type="button"
                                className="btn btn-danger btn-sm mt-2"
                                onClick={() => handleRemoveField(index)}
                            >
                                Remove
                            </button>
                        </div>
                    ))}

                    <button
                        type="button"
                        className="btn btn-success btn-sm mt-2"
                        onClick={handleAddField}
                    >
                        Add Another Field
                    </button>
                </div>
            ) : (
                <select
                    id={id}
                    name={name}
                    className="form-select"
                    value={selectedValues}
                    onChange={handleChange}
                    disabled={disabled}
                    required={required}
                >
                    <option value="" disabled>
                        {placeholder ? `Select ${placeholder}` : `Select ${label || name}`}
                    </option>
                    {options.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            )}
        </div>
    );
};

export default SelectField;
